// src/pages/TeamPerformancePage.tsx
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { performanceApi } from '../api';
import {
  Box, Typography, CircularProgress, Alert, Paper, Grid,
  Container, Card, CardContent, Table, TableBody, TableCell,
  TableHead, TableRow, LinearProgress, Chip, FormControl,
  InputLabel, Select, MenuItem, Tabs, Tab, Avatar
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon, Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon, Person as PersonIcon,
  Timeline as TimelineIcon, Speed as SpeedIcon
} from '@mui/icons-material';
import type { UserPerformance } from '../types/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`performance-tabpanel-${index}`}
      aria-labelledby={`performance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `performance-tab-${index}`,
    'aria-controls': `performance-tabpanel-${index}`,
  };
}

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to get performance color
const getPerformanceColor = (percentage: number): 'success' | 'warning' | 'error' => {
  if (percentage >= 90) return 'success';
  if (percentage >= 70) return 'warning';
  return 'error';
};

// Helper function to get user initials
const getUserInitials = (userId: number): string => {
  return `U${userId}`;
};

export function TeamPerformancePage() {
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [tabValue, setTabValue] = useState(0);

  // Fetch team performance data
  const { data: teamPerformance, isLoading, isError, error } = useQuery<UserPerformance[]>({
    queryKey: ['performance', 'team', selectedPeriod],
    queryFn: async () => {
      try {
        // Use the correct API endpoint based on the backend implementation
        return await performanceApi.getUserPerformance();
      } catch (error) {
        console.error('Error fetching team performance:', error);
        throw error;
      }
    }
  });

  // Calculate team metrics
  const teamMetrics = useMemo(() => {
    if (!teamPerformance || teamPerformance.length === 0) {
      return {
        totalTeamMembers: 0,
        avgQuotaAttainment: 0,
        totalDealsWon: 0,
        totalPipelineGenerated: 0,
        avgWinRate: 0,
        topPerformer: null
      };
    }

    const totalTeamMembers = teamPerformance.length;
    const avgQuotaAttainment = teamPerformance.reduce((sum, p) => sum + p.quota_attainment, 0) / totalTeamMembers;
    const totalDealsWon = teamPerformance.reduce((sum, p) => sum + p.deals_won, 0);
    const totalPipelineGenerated = teamPerformance.reduce((sum, p) => sum + p.pipeline_generated, 0);

    const avgWinRate = teamPerformance.reduce((sum, p) => {
      const totalDeals = p.deals_won + p.deals_lost;
      return sum + (totalDeals > 0 ? (p.deals_won / totalDeals) * 100 : 0);
    }, 0) / totalTeamMembers;

    const topPerformer = teamPerformance.reduce((top, current) =>
      current.quota_attainment > (top?.quota_attainment || 0) ? current : top
      , teamPerformance[0]);

    return {
      totalTeamMembers,
      avgQuotaAttainment,
      totalDealsWon,
      totalPipelineGenerated,
      avgWinRate,
      topPerformer
    };
  }, [teamPerformance]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load team performance data. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Team Performance Dashboard
        </Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            label="Period"
            onChange={(e) => handlePeriodChange(e.target.value)}
          >
            <MenuItem value="current">Current Period</MenuItem>
            <MenuItem value="2024-Q1">Q1 2024</MenuItem>
            <MenuItem value="2024-Q2">Q2 2024</MenuItem>
            <MenuItem value="2024-Q3">Q3 2024</MenuItem>
            <MenuItem value="2024-Q4">Q4 2024</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Team Overview Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon color="primary" fontSize="large" />
                <Box>
                  <Typography variant="h6">{teamMetrics.totalTeamMembers}</Typography>
                  <Typography variant="body2" color="text.secondary">Team Members</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="success" fontSize="large" />
                <Box>
                  <Typography variant="h6">{teamMetrics.avgQuotaAttainment.toFixed(1)}%</Typography>
                  <Typography variant="body2" color="text.secondary">Avg Quota Attainment</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrophyIcon color="warning" fontSize="large" />
                <Box>
                  <Typography variant="h6">{teamMetrics.totalDealsWon}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Deals Won</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MoneyIcon color="info" fontSize="large" />
                <Box>
                  <Typography variant="h6">{formatCurrency(teamMetrics.totalPipelineGenerated)}</Typography>
                  <Typography variant="body2" color="text.secondary">Pipeline Generated</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="performance tabs">
            <Tab label="Individual Performance" {...a11yProps(0)} />
            <Tab label="Leaderboard" {...a11yProps(1)} />
            <Tab label="Team Analytics" {...a11yProps(2)} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Individual Performance Table */}
          <Typography variant="h6" gutterBottom>
            Individual Performance Metrics
          </Typography>
          {!teamPerformance || teamPerformance.length === 0 ? (
            <Alert severity="info">
              No performance data available for the selected period.
            </Alert>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Team Member</TableCell>
                  <TableCell>Quota Attainment</TableCell>
                  <TableCell>Deals Won</TableCell>
                  <TableCell>Deals Lost</TableCell>
                  <TableCell>Win Rate</TableCell>
                  <TableCell>Pipeline Generated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamPerformance.map((performance) => {
                  const totalDeals = performance.deals_won + performance.deals_lost;
                  const winRate = totalDeals > 0 ? (performance.deals_won / totalDeals) * 100 : 0;

                  return (
                    <TableRow key={performance.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {getUserInitials(performance.user_id)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              User {performance.user_id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {performance.period}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(performance.quota_attainment, 100)}
                              color={getPerformanceColor(performance.quota_attainment)}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ minWidth: 35 }}>
                            {performance.quota_attainment.toFixed(1)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={performance.deals_won}
                          color="success"
                          size="small"
                          icon={<TrophyIcon />}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={performance.deals_lost}
                          color="error"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {winRate.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(performance.pipeline_generated)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Leaderboard */}
          <Typography variant="h6" gutterBottom>
            Performance Leaderboard
          </Typography>
          {!teamPerformance || teamPerformance.length === 0 ? (
            <Alert severity="info">
              No performance data available for leaderboard.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {/* Top Performers by Quota Attainment */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Quota Achievers
                    </Typography>
                    {teamPerformance
                      .sort((a, b) => b.quota_attainment - a.quota_attainment)
                      .slice(0, 5)
                      .map((performance, index) => (
                        <Box key={performance.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" color="primary">
                              #{index + 1}
                            </Typography>
                            <Avatar sx={{ bgcolor: index === 0 ? 'gold' : 'primary.main' }}>
                              {getUserInitials(performance.user_id)}
                            </Avatar>
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              User {performance.user_id}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(performance.quota_attainment, 100)}
                              color={getPerformanceColor(performance.quota_attainment)}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                          <Typography variant="body2" fontWeight="bold">
                            {performance.quota_attainment.toFixed(1)}%
                          </Typography>
                        </Box>
                      ))}
                  </CardContent>
                </Card>
              </Grid>

              {/* Top Performers by Deals Won */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Most Deals Won
                    </Typography>
                    {teamPerformance
                      .sort((a, b) => b.deals_won - a.deals_won)
                      .slice(0, 5)
                      .map((performance, index) => (
                        <Box key={performance.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" color="success.main">
                              #{index + 1}
                            </Typography>
                            <Avatar sx={{ bgcolor: index === 0 ? 'success.main' : 'primary.main' }}>
                              {getUserInitials(performance.user_id)}
                            </Avatar>
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              User {performance.user_id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {performance.deals_lost} deals lost
                            </Typography>
                          </Box>
                          <Chip
                            label={performance.deals_won}
                            color="success"
                            size="small"
                            icon={<TrophyIcon />}
                          />
                        </Box>
                      ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Team Analytics */}
          <Typography variant="h6" gutterBottom>
            Team Analytics & Insights
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Team Summary
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Average Win Rate:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {teamMetrics.avgWinRate.toFixed(1)}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Total Pipeline:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(teamMetrics.totalPipelineGenerated)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Top Performer:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {teamMetrics.topPerformer ? `User ${teamMetrics.topPerformer.user_id}` : 'N/A'}
                      </Typography>
                    </Box>
                    {teamMetrics.topPerformer && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Top Performance:</Typography>
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          {teamMetrics.topPerformer.quota_attainment.toFixed(1)}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Performance Distribution
                  </Typography>
                  {teamPerformance && teamPerformance.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" gutterBottom>
                          Quota Attainment Ranges:
                        </Typography>
                        {(() => {
                          const ranges = {
                            'Above 100%': teamPerformance.filter(p => p.quota_attainment >= 100).length,
                            '80-99%': teamPerformance.filter(p => p.quota_attainment >= 80 && p.quota_attainment < 100).length,
                            '60-79%': teamPerformance.filter(p => p.quota_attainment >= 60 && p.quota_attainment < 80).length,
                            'Below 60%': teamPerformance.filter(p => p.quota_attainment < 60).length,
                          };

                          return Object.entries(ranges).map(([range, count]) => (
                            <Box key={range} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2">{range}:</Typography>
                              <Chip
                                label={count}
                                size="small"
                                color={
                                  range === 'Above 100%' ? 'success' :
                                    range === '80-99%' ? 'warning' :
                                      range === '60-79%' ? 'info' : 'error'
                                }
                              />
                            </Box>
                          ));
                        })()}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No data available for analysis
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
}
